import React, { useEffect, useState } from 'react';
import {
  Badge,
  Card,
  Row,
  Col,
  Container,
  Form,
  Button
} from 'react-bootstrap';
import moment from "moment";
import { convertCurrency, numberFormat, convertOrderStatus } from '../../helper';
import { GET_EXCHANGE_RATE } from '../../apollo/product/query';
import { useLazyQuery, useMutation } from '@apollo/client';
import { COLOR_WHITE, imageRender, PRODUCTION_STATUS_PERMISSIONS, PRODUCTION_STATUS_FLOW, USER_KEY, checkProductionStatusPermission } from '../../const';
import { useSelector } from 'react-redux';
import { FaLuggageCart } from 'react-icons/fa';
import { useTranslation } from "react-i18next";
import noProductImg from "../../images/no_product.png";
import { UPDATE_PRODUCTION_STATUS } from '../../apollo/ordergroup/query';
import { successSwal, errorSwal } from '../../helper/sweetalert';

const productionStatusKeys = [
  { value: "PRODUCTION_ORDER", labelKey: "ps_production_order" },
  { value: "PATTERN", labelKey: "ps_pattern" },
  { value: "PRINTING", labelKey: "ps_printing" },
  { value: "HEAT_TRANSFER", labelKey: "ps_heat_transfer" },
  { value: "CUTTING", labelKey: "ps_cutting" },
  { value: "SEWING", labelKey: "ps_sewing" },
  { value: "QC", labelKey: "ps_qc" },
  { value: "READY_TO_COMPLETE", labelKey: "ps_ready_to_complete" },
  { value: "COMPLETED", labelKey: "ps_completed" }
];

const convertOrderType = (type, t) => {
  switch (type) {
    case 'POS':
      return t("order_type_pos");
    case 'ONLINE':
      return t("order_type_online");
    case 'LIVE':
      return t("order_type_live");
    case 'SALE_PAGE':
      return t("order_type_salepage");
    default:
      return type || '-';
  }
};

const convertLogicticsName = (name) => {
  return name || "-";
};

const convertPriceWithCommission = (totalPrice, sumPriceBaht, sumPriceUsd, commission, rate) => {
  const kip = Number(totalPrice ?? 0) || 0;
  const baht = Number(sumPriceBaht ?? 0) || 0;
  const usd = Number(sumPriceUsd ?? 0) || 0;
  const extra = Number(commission ?? 0) || 0;

  const bahtRate = Number(rate?.baht) > 0 ? Number(rate?.baht) : 1;
  const usdRate = Number(rate?.usd) > 0 ? Number(rate?.usd) : 1;

  return kip + baht * bahtRate + usd * usdRate + extra;
};
const mapStatusColor = (status) => {
  switch (status) {
    case "PRODUCTION_ORDER":
      return "secondary";
    case "PATTERN":
      return "info";
    case "PRINTING":
      return "primary";
    case "HEAT_TRANSFER":
      return "warning";
    case "CUTTING":
      return "danger";
    case "SEWING":
      return "primary";
    case "QC":
      return "info";
    case "READY_TO_COMPLETE":
      return "warning";
    case "COMPLETED":
      return "success";
    default:
      return "light";
  }
};
const mapStatusText = (status) => {
  switch (status) {
    case "HEAT_TRANSFER":
      return "dark";
    default:
      return "white";
  }
};

const pickFirstNonEmptyString = (value) => {
  if (!Array.isArray(value)) return "";
  const first = value.find((v) => typeof v === "string" && v.trim());
  return first || "";
};

const getOrderImageSrc = (order, emptyImage) => {
  const stockImage = pickFirstNonEmptyString(order?.stock?.image);
  if (stockImage) return imageRender(stockImage);

  const variantImage = pickFirstNonEmptyString(order?.productVariant?.image);
  if (variantImage) return imageRender(variantImage);

  return emptyImage;
};

export default function SalepageOrderDetailNew({ selectedOrderGroup }) {
  const { t } = useTranslation();
  const productionStatuses = productionStatusKeys.map((s) => ({ value: s.value, label: t(s.labelKey) }));
  const [shopRate, setShopRate] = useState();
  const { rolePermissions } = useSelector((state) => state.permissions);
  const userData = JSON.parse(localStorage.getItem(USER_KEY));
  const isAdmin = userData?.data?.role === "ADMIN";
  const canEditReceiveDate = isAdmin || rolePermissions.includes("SALE_ACCESS") || rolePermissions.includes("REPORT_SALE_ONLY") || rolePermissions.includes("CAN_UPDATE_PRODUCTION_ORDER");
  const updatableStatuses = productionStatuses.filter(status => 
      checkProductionStatusPermission(status.value, rolePermissions)
    );
    const allowedStatuses = updatableStatuses.map(s => s.value);
    const visibleStatuses = Array.from(new Set([
      ...allowedStatuses,
      ...allowedStatuses.map(status => PRODUCTION_STATUS_FLOW[status]).filter(Boolean)
    ]));
    const [selectedStatus, setSelectedStatus] = useState(selectedOrderGroup?.productionStatus || "PRODUCTION_ORDER");
  const [receiveDate, setReceiveDate] = useState(() => ((selectedOrderGroup?.receiveDate ?? selectedOrderGroup?.recieveDate ?? selectedOrderGroup?.checkBillDate)
    ? moment.utc(selectedOrderGroup?.receiveDate ?? selectedOrderGroup?.recieveDate ?? selectedOrderGroup?.checkBillDate).local().format("DD/MM/YYYY")
    : ""));
  const [getExchangeRate, { data: loadExchangeRate }] = useLazyQuery(GET_EXCHANGE_RATE, { fetchPolicy: "cache-and-network" });
  const [updateProductionStatus, { loading: updatingStatus }] = useMutation(UPDATE_PRODUCTION_STATUS);

  useEffect(() => {
    if (loadExchangeRate) {
      setShopRate(loadExchangeRate?.exchangeRate);
    }
  }, [loadExchangeRate]);

  useEffect(() => {
    if (selectedOrderGroup?.shop?.id) {
      getExchangeRate({
        variables: {
          where: {
            shop: selectedOrderGroup?.shop?.id,
          },
        },
      });
    }
  }, [selectedOrderGroup, getExchangeRate]);

  useEffect(() => {
    if (selectedOrderGroup?.productionStatus) {
      setSelectedStatus(selectedOrderGroup.productionStatus);
    }
  }, [selectedOrderGroup]);

  useEffect(() => {
    const nextDate = selectedOrderGroup?.receiveDate ?? selectedOrderGroup?.recieveDate ?? selectedOrderGroup?.checkBillDate;
    setReceiveDate(
      nextDate
        ? moment.utc(nextDate).local().format("DD/MM/YYYY")
        : ""
    );
  }, [selectedOrderGroup]);

  const handleUpdateStatus = async () => {
    try {
      let utcDateTime = null;
      if (canEditReceiveDate && receiveDate) {
        const parsed = moment(receiveDate, "DD/MM/YYYY", true);
        if (!parsed.isValid()) throw new Error("Invalid receive date");
        utcDateTime = parsed.startOf("day").toISOString();
      }

      let nextStatus = selectedStatus;
      if (!isAdmin) {
        // Automatically move to the next status in the flow
        nextStatus = PRODUCTION_STATUS_FLOW[selectedOrderGroup?.productionStatus] || selectedOrderGroup?.productionStatus;
      }

      await updateProductionStatus({
        variables: {
          where: { id: selectedOrderGroup?.id },
          data: {
            status: nextStatus,
            ...(utcDateTime ? { receiveDate: utcDateTime } : {})
          },
        }
      });
      successSwal(t("update_success"));
    } catch (error) {
      errorSwal(error.message);
    }
  };

  if (!selectedOrderGroup) return null;
  const createdAtText = moment(selectedOrderGroup?.createdAt).isValid()
    ? moment(selectedOrderGroup?.createdAt).format("DD/MM/YYYY HH:mm")
    : (selectedOrderGroup?.createdAt ?? "-");

  const productionLabel = productionStatuses.find((status) => status.value === selectedOrderGroup?.productionStatus)?.label;

  return (
    <Container className="py-3">
      <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6>{t("customer_label")}: {selectedOrderGroup?.cfName ?? selectedOrderGroup?.customerName ?? "-"}</h6>
              <p>{t("phone_label")}: {selectedOrderGroup?.phone ?? "-"}</p>
              <p>{t("date_label")} {createdAtText}</p>
            </Col>
            <Col md={6}>
              <p>
                {t("payment_label")}: {" "}
                <Badge bg={selectedOrderGroup?.isPaymented ? "success" : "danger"}>
                  {selectedOrderGroup?.isPaymented ? t("paid") : t("not_paid")}
                </Badge>
              </p>
              <p>{t("pay_shipping_label")}: {selectedOrderGroup?.orderDelivery?.shipmentPayType === "COD" ? t("payment_destination") : t("payment_origin")}</p>
              <p>
                {t("order_status_label")}:{" "}
                <Badge bg={mapStatusColor(selectedOrderGroup?.productionStatus)} text={mapStatusText(selectedOrderGroup?.productionStatus)}>
                  {productionLabel || convertOrderStatus(selectedOrderGroup?.status)}
                </Badge>
              </p>
              <p>{t("type_label")}: {convertOrderType(selectedOrderGroup?.type, t)}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {(isAdmin || visibleStatuses.includes(selectedOrderGroup?.productionStatus)) && (
        <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
          <Card.Body>
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Label>{t("ເລືອກຕຳແໜ່ງ")}</Form.Label>
                {isAdmin ? (
                  <Form.Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    {productionStatuses.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </Form.Select>
                ) : (
                  <div className="p-2 border rounded bg-light" style={{ fontWeight: "bold", height: 38 }}>
                    {checkProductionStatusPermission(selectedOrderGroup?.productionStatus, rolePermissions) 
                      ? (productionStatuses.find(s => s.value === PRODUCTION_STATUS_FLOW[selectedOrderGroup?.productionStatus])?.label || "-")
                      : (productionStatuses.find(s => s.value === selectedOrderGroup?.productionStatus)?.label || "-")
                    }
                  </div>
                )}
              </Col>
              <Col md={4}>
                <Form.Label>ວັນທີຮັບເຄື່ອງ</Form.Label>
                <Form.Control
                  type="date"
                  value={
                    receiveDate
                      ? moment(receiveDate, "DD/MM/YYYY", true).format("YYYY-MM-DD")
                      : ""
                  }
                  onChange={(e) => {
                    const iso = e?.target?.value;
                    setReceiveDate(iso ? moment(iso).format("DD/MM/YYYY") : "");
                  }}
                  style={{ width: "100%" }}
                  disabled={!canEditReceiveDate}
                />
              </Col>
              <Col md={2}>
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={handleUpdateStatus}
                  disabled={updatingStatus || selectedOrderGroup?.productionStatus === "COMPLETED" || (!isAdmin && !checkProductionStatusPermission(selectedOrderGroup?.productionStatus, rolePermissions) && !canEditReceiveDate)}
                >
                  {updatingStatus ? "..." : t("update_status")}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
        <Card.Body>
          <Card.Text>{t("order_items_title")}</Card.Text>
          {selectedOrderGroup?.orders?.map((item) => (
            <Row key={item.id} className="align-items-center mb-3 border-bottom">
              <Col md={2}>
                <img
                  src={getOrderImageSrc(item, noProductImg)}
                  alt={item?.cfMessage}
                  className="img-fluid rounded"
                  style={{ width: 60 }}
                />
              </Col>
              <Col md={6}>
                <h6 className="mb-1">{item?.productName}</h6>
                <p className="text-muted mb-0">{t("code_label")}: {item?.cfMessage || item?.stock?.cfMessage}</p>
              </Col>
              <Col md={2} className="text-end">
                <p className="mb-0">{convertCurrency(item?.currency)} {numberFormat(item?.totalPrice)}</p>
              </Col>
              <Col md={2} className="text-end">
                <p className="mb-0">{t("quantity_label")}: ({item?.amount})</p>
              </Col>
            </Row>
          ))}
        </Card.Body>
      </Card>

      <Row>
        <Col md={6}>
          <Card style={{ boxShadow: 'none', background: COLOR_WHITE }} className="mb-3">
            <Card.Body>
              <h5>
                {t("delivery")} {convertLogicticsName(
                  selectedOrderGroup?.orderDelivery?.delivery || selectedOrderGroup?.logistic || ""
                )}
              </h5>
              <p>
                <strong>{t("tracking_no_label")}:</strong> {selectedOrderGroup?.orderDelivery?.deliveryTackingNo ?? "-"}
              </p>

              {selectedOrderGroup?.orderDelivery?.id ? <p>
                <strong>{t("delivery_address_label")}:</strong> ({selectedOrderGroup?.orderDelivery?.receiver?.branchCode ?? "-"}), {selectedOrderGroup?.orderDelivery?.receiver?.province}, {selectedOrderGroup?.orderDelivery?.receiver?.district},{' '}
                {selectedOrderGroup?.orderDelivery?.receiver?.branch}
              </p> :
                <p>
                  <strong>{t("delivery_address_label")}: {selectedOrderGroup?.destinationLogistic ?? "-"} </strong>
                </p>
              }

              <p className="text-success">
                <FaLuggageCart style={{ fontSize: 28, marginRight: 6 }} />
                {t("other_shipping_label")}: {selectedOrderGroup?.addressCf ?? ""}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={{ boxShadow: 'none', background: COLOR_WHITE }}>
            <Card.Body>
              <h5>{t("order_summary_title")}</h5>
              <Row className="mb-2 text-muted">
                <Col>{t("total_kip_label")}:</Col>
                <Col className="text-end">{numberFormat(selectedOrderGroup?.totalPrice ?? 0)}</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("total_baht_label")}:</Col>
                <Col className="text-end">{numberFormat(selectedOrderGroup?.sumPriceBaht ?? 0)}</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("total_usd_label")}:</Col>
                <Col className="text-end">{numberFormat(selectedOrderGroup?.sumPriceUsd ?? 0)}</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("tax_label")}:</Col>
                <Col className="text-end">0</Col>
              </Row>
              <hr />
              <Row className="fw-bold">
                <Col>{t("amount_paid_label")}:</Col>
                <Col className="text-end">{numberFormat(convertPriceWithCommission(selectedOrderGroup?.totalPrice, selectedOrderGroup?.sumPriceBaht, selectedOrderGroup?.sumPriceUsd, 0, shopRate))} {t("kip_currency")}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
